import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Loader2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getVisibleTiers, type TierEnum } from '@/constants/tiers';
import { useEntitlements } from '@/contexts/EntitlementContext';
import { useTierCatalog } from '@/hooks/useTierCatalog';
import { useAuth } from '@/contexts/AuthContext';
import { cloudClient } from '@/integrations/supabase/cloudClient';
import { getStripeEnvironment } from '@/lib/stripeEnv';
import { useToast } from '@/hooks/use-toast';
import { ctaHrefFor } from '@/lib/checkoutPricing';

const ctaLabelFor = (tier: TierEnum, displayName: string, isLoggedIn: boolean): string => {
  // "Start Configuring" is only honest once you're signed in — the custom
  // configurator needs an account, so signed-out visitors are offered the
  // free account instead of being bounced to /auth mid-form.
  if (tier === 'free') return isLoggedIn ? 'Start Configuring' : 'Create free account';
  if (!isLoggedIn) return 'Create free account';
  return `Get ${displayName}`;
};


interface TiersSectionProps {
  /** 'home' (default) shows action CTAs; 'pricing' shows disabled Coming soon / Your plan. */
  variant?: 'home' | 'pricing';
}

// Renders the live tier catalog (subscription_plans + feature_flags) as marketing cards.
// Falls back to static tier metadata while the catalog is loading.
const TiersSection: React.FC<TiersSectionProps> = ({ variant = 'home' }) => {
  const { memberships, loading: entLoading } = useEntitlements();
  const { catalog, loading } = useTierCatalog();
  const { user } = useAuth();
  const { toast } = useToast();
  const [portalLoading, setPortalLoading] = React.useState<TierEnum | null>(null);
  const visibleStatic = getVisibleTiers();
  const isPricing = variant === 'pricing';
  // Only trust membership state once entitlements have actually loaded — otherwise
  // every card flashes into the disabled "Your plan" state during hydration and
  // swallows the user's click on the real CTA.
  const currentKeys = new Set(entLoading ? [] : memberships.map((m) => m.key));

  const openPortal = async (tier: TierEnum) => {
    setPortalLoading(tier);
    try {
      const { data, error } = await cloudClient.functions.invoke('create-portal-session', {
        body: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/pricing`,
        },
      });
      if (error || !data?.url) throw new Error(error?.message || data?.error || 'Could not open billing portal');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast({
        title: 'Unable to open billing portal',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPortalLoading(null);
    }
  };

  // Build a render list keyed by the static visibility order so layout stays stable.
  const items = visibleStatic.map((meta) => {
    const entry = catalog.find((c) => c.key === meta.enum);
    return {
      key: meta.enum,
      displayName: entry?.displayName ?? meta.displayName,
      tagline: entry?.tagline ?? meta.tagline,
      description: entry?.description ?? meta.description,
      priceMonthly: entry?.priceMonthly ?? meta.priceMonthly,
      bullets: entry
        ? [...entry.featureLabels, ...entry.panelCategoryLabels]
        : [...meta.featureLabels, ...meta.panelCategoryLabels],
    };
  });

  return (
    <section className="relative z-20 px-4 sm:px-6 py-12 sm:py-16">
      <div className="container mx-auto max-w-7xl">
        {!isPricing && (
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 drop-shadow-lg">
              Choose how you want to use it
            </h2>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
              Pick one tier or combine several — US$10/mo per tier, +US$5/mo for each extra. Free tier is always free.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4">
          {items.map((tier) => {
            const isFree = tier.key === 'free';
            const isCurrent = currentKeys.has(tier.key);
            return (
              <div
                key={tier.key}
                className={cn(
                  'relative flex flex-col rounded-xl border p-5 backdrop-blur-md transition-all duration-200',
                  isCurrent
                    ? 'border-brand-blue/60 bg-brand-blue/5 ring-2 ring-brand-blue/40'
                    : 'border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25',
                )}
              >
                <div className="space-y-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white">{tier.displayName}</h3>
                      {tier.key === 'touring' && (
                        <span className="inline-block px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[10px] font-semibold uppercase tracking-wide">
                          Beta
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/60">{tier.tagline}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-white">
                      {isFree ? 'Free' : `$${tier.priceMonthly}`}
                      {!isFree && <span className="text-xs font-normal text-white/60">/mo USD</span>}
                    </span>
                  </div>

                  <p className="text-xs text-white/75 leading-snug">{tier.description}</p>

                  {loading && tier.bullets.length === 0 ? (
                    <div className="space-y-1 pt-1">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="h-3 rounded bg-white/10 animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-1 pt-1">
                      {tier.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-1.5 text-xs text-white/85">
                          <Check className="h-3 w-3 mt-0.5 text-brand-blue flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {!isFree && (
                    <div className="mt-3 flex items-start gap-1.5 rounded-md border border-brand-blue/25 bg-brand-blue/5 px-2 py-1.5">
                      <Users className="h-3 w-3 mt-0.5 text-brand-blue-light flex-shrink-0" aria-hidden="true" />
                      <div className="text-[11px] leading-snug">
                        <span className="font-semibold text-white">Team licenses available</span>
                        <span className="text-white/60"> — from 5 seats, save up to 40%</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-auto pt-5">
                  {isPricing && isCurrent && isFree ? (
                    <button
                      type="button"
                      disabled
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold bg-brand-blue/20 text-white border border-brand-blue/50 cursor-default"
                    >
                      Your plan
                    </button>
                  ) : isPricing && isCurrent && !isFree ? (
                    <button
                      type="button"
                      onClick={() => openPortal(tier.key)}
                      disabled={portalLoading === tier.key}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold bg-brand-blue/20 text-white border border-brand-blue/50 hover:bg-brand-blue/30 transition-all duration-200 disabled:opacity-60"
                    >
                      {portalLoading === tier.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Manage subscription
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  ) : (
                    <>
                      <Link
                        to={ctaHrefFor(tier.key, !!user)}
                        className={cn(
                          'w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all duration-200',
                          'bg-white/10 text-white border border-white/20 hover:bg-white/15',
                        )}
                      >
                        {ctaLabelFor(tier.key, tier.displayName, !!user)}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                      {isFree && !user && (
                        <p className="mt-2 text-center text-xs text-white/60">Free account, no card</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!isPricing && (
          <div className="mt-6 space-y-1 text-center">
            <p className="text-xs text-white/50">
              Combine tiers for full access: US$10 for one, US$15 for two, US$20 for all three paid tiers. All prices in USD.
            </p>
            <p className="text-xs text-white/60">
              Buying for a team? Every paid tier is available as a per-seat team license — pick seats at checkout.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default TiersSection;
